// EssayCadernoPanel — painel lateral durante a escrita da redação, mostrando
// o caderno da prova (instruções + proposta + direcionadores + fragmentos) que
// o aluno aplicou da Biblioteca de Redação OU configurou manualmente.
//
// Comportamento:
// - Recolhe TOTAL via botão (vira uma barra estreita à direita com ícone de
//   livro + botão pra expandir, idêntico ao padrão de sidebar do projeto).
// - Cada seção (Instruções / Proposta / Direcionadores / cada Fragmento) é
//   colapsável individualmente.
// - Estado persistido em localStorage por sessão (chave por id).
//
// [[project-essay-themes-library]]

import { useEffect, useState } from "preact/hooks";
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ExternalLink as PopoutIcon,
  FileText,
  Image as ImageIcon,
  Maximize2,
  PanelRightClose,
  PanelRightOpen,
  Sparkles,
  Table as TableIcon,
  X
} from "lucide-react";
import { renderEssayMarkdown } from "@app/ui/lib/essay-markdown";
import type { EssayFragment, EssayMeta } from "./api";

const STORAGE_PREFIX_OPEN = "essay-caderno-open:";
const STORAGE_PREFIX_SECTIONS = "essay-caderno-sections:";
const STORAGE_PANEL_WIDTH = "essay-caderno-panel-width";
const PANEL_WIDTH_MIN = 320;
const PANEL_WIDTH_MAX = 760;
const PANEL_WIDTH_DEFAULT = 460;

function loadPanelWidth(): number {
  try {
    const raw = localStorage.getItem(STORAGE_PANEL_WIDTH);
    const n = raw ? parseInt(raw, 10) : NaN;
    if (!Number.isFinite(n)) return PANEL_WIDTH_DEFAULT;
    return Math.max(PANEL_WIDTH_MIN, Math.min(PANEL_WIDTH_MAX, n));
  } catch {
    return PANEL_WIDTH_DEFAULT;
  }
}

function savePanelWidth(w: number) {
  try {
    localStorage.setItem(STORAGE_PANEL_WIDTH, String(w));
  } catch {
    /* ignore */
  }
}

interface Props {
  sessionId: string;
  meta: EssayMeta | null;
  fragments: EssayFragment[];
  /** Tema/título da sessão (fallback quando não é da biblioteca). */
  title: string;
  /** Modo de visualização atual. Default "side". */
  viewMode?: "side" | "modal" | "popout";
  /** Callback quando aluno troca modo de visualização. */
  onViewModeChange?: (mode: "side" | "modal" | "popout") => void;
  /** Se true, o aluno está vendo via janela popup (rota dedicada) e não
   *  deve mostrar os botões de troca de modo. */
  isPopoutWindow?: boolean;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return iso;
  }
}

function loadOpen(sessionId: string): boolean {
  try {
    const v = localStorage.getItem(STORAGE_PREFIX_OPEN + sessionId);
    return v === null ? true : v === "1";
  } catch {
    return true;
  }
}

function saveOpen(sessionId: string, open: boolean) {
  try {
    localStorage.setItem(STORAGE_PREFIX_OPEN + sessionId, open ? "1" : "0");
  } catch {
    /* ignore */
  }
}

function loadSections(sessionId: string): Record<string, boolean> {
  try {
    const v = localStorage.getItem(STORAGE_PREFIX_SECTIONS + sessionId);
    return v ? JSON.parse(v) : {};
  } catch {
    return {};
  }
}

function saveSections(sessionId: string, sections: Record<string, boolean>) {
  try {
    localStorage.setItem(STORAGE_PREFIX_SECTIONS + sessionId, JSON.stringify(sections));
  } catch {
    /* ignore */
  }
}

function Section({
  id,
  title,
  icon: Icon,
  defaultOpen = true,
  sections,
  setSections,
  accent,
  children
}: {
  id: string;
  title: string;
  icon: typeof BookOpen;
  defaultOpen?: boolean;
  sections: Record<string, boolean>;
  setSections: (next: Record<string, boolean>) => void;
  accent?: "amber" | "violet" | "stone";
  children: any;
}) {
  const isOpen = sections[id] ?? defaultOpen;
  const accentClass =
    accent === "amber"
      ? "border-amber-400/50 bg-amber-50/40 dark:bg-amber-900/10"
      : accent === "violet"
        ? "border-violet-400/40 bg-violet-50/40 dark:bg-violet-900/10"
        : "border-border bg-background";

  function toggle() {
    setSections({ ...sections, [id]: !isOpen });
  }

  return (
    <div class={`rounded-md border ${accentClass}`}>
      <button
        type="button"
        onClick={toggle}
        class="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span class="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600 dark:text-stone-300">
          <Icon className="size-3.5" />
          {title}
        </span>
        {isOpen ? (
          <ChevronDown className="size-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-3.5 text-muted-foreground" />
        )}
      </button>
      {isOpen && <div class="border-t border-border/40 px-3 pb-3 pt-2 text-[13px]">{children}</div>}
    </div>
  );
}

function FonteLine({ source }: { source: string }) {
  if (!source?.trim()) return null;
  const stripped = source.trim();
  const alreadyHasFonte = /^(fonte|font|f\.)\s*:/i.test(stripped);
  return (
    <p
      class="mt-1.5 text-[11px] italic text-muted-foreground"
      dangerouslySetInnerHTML={{
        __html:
          (alreadyHasFonte ? "" : "<strong>Fonte:</strong> ") +
          renderEssayMarkdown(stripped)
      }}
    />
  );
}

function FragmentBody({ fragment }: { fragment: EssayFragment }) {
  if (fragment.kind === "image") {
    return (
      <div>
        {fragment.imageDataUrl ? (
          <figure>
            <img
              src={fragment.imageDataUrl}
              alt={fragment.textContent || fragment.label}
              class="mx-auto max-h-72 rounded border border-border"
            />
            {fragment.textContent && (
              <figcaption class="mt-1 text-center text-[11px] italic text-muted-foreground">
                {fragment.textContent}
              </figcaption>
            )}
          </figure>
        ) : (
          <div class="rounded border border-dashed border-border px-3 py-2 text-center text-xs text-muted-foreground">
            <ImageIcon className="mx-auto mb-1 size-4 opacity-60" />
            {fragment.textContent || "[imagem]"}
          </div>
        )}
      </div>
    );
  }

  if (fragment.kind === "table" && fragment.tableData) {
    return (
      <div class="overflow-x-auto">
        <table class="w-full border-collapse text-[12px]">
          <thead>
            <tr>
              {(fragment.tableData.headers ?? []).map((h, i) => (
                <th
                  key={i}
                  class="border border-border bg-muted/50 px-2 py-1 text-left font-semibold"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(fragment.tableData.rows ?? []).map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} class="border border-border px-2 py-1">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // text / composite (fallback)
  if (fragment.textContent) {
    return (
      <div
        class="essay-paper text-[13px]"
        dangerouslySetInnerHTML={{ __html: renderEssayMarkdown(fragment.textContent) }}
      />
    );
  }
  return null;
}

function fragmentIcon(kind: EssayFragment["kind"]): typeof FileText {
  if (kind === "image") return ImageIcon;
  if (kind === "table") return TableIcon;
  return FileText;
}

export function EssayCadernoPanel({
  sessionId,
  meta,
  fragments,
  title,
  viewMode = "side",
  onViewModeChange,
  isPopoutWindow = false
}: Props) {
  const [open, setOpen] = useState<boolean>(() => loadOpen(sessionId));
  const [sections, setSectionsState] = useState<Record<string, boolean>>(() =>
    loadSections(sessionId)
  );
  const [panelWidth, setPanelWidth] = useState<number>(() => loadPanelWidth());
  const [resizing, setResizing] = useState<boolean>(false);

  function startResize(e: MouseEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = panelWidth;
    setResizing(true);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    function onMove(ev: MouseEvent) {
      const delta = startX - ev.clientX;
      const next = Math.max(
        PANEL_WIDTH_MIN,
        Math.min(PANEL_WIDTH_MAX, startW + delta)
      );
      setPanelWidth(next);
    }
    function onUp() {
      setResizing(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const w = parseInt(
        document.documentElement.style.getPropertyValue("--last-panel-w") || "0",
        10
      );
      if (Number.isFinite(w)) {
        /* noop — width já está no state */
      }
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  useEffect(() => {
    if (!resizing) savePanelWidth(panelWidth);
  }, [resizing, panelWidth]);

  function openInModal() {
    onViewModeChange?.("modal");
  }
  function openInPopout() {
    const url = `${window.location.origin}/student/caderno-popup/${encodeURIComponent(
      sessionId
    )}`;
    const w = window.open(url, "caderno-" + sessionId, "width=520,height=900");
    if (w) {
      onViewModeChange?.("popout");
    }
  }
  function closeModal() {
    onViewModeChange?.("side");
  }

  useEffect(() => saveOpen(sessionId, open), [open, sessionId]);
  function setSections(next: Record<string, boolean>) {
    setSectionsState(next);
    saveSections(sessionId, next);
  }

  // Quando há tema da biblioteca, exibe metadados; senão usa só o título.
  const fromLibrary = !!(meta?.fromThemeSlug || meta?.statement);
  const banca = meta?.banca;
  const headerTitle = fromLibrary && meta?.examEditionKey ? title : title;

  // Modos de visualização:
  //   side  → coluna lateral à direita do editor, responsiva via clamp
  //   modal → overlay full-screen com backdrop blur
  //   popout → janela popup separada (rota dedicada)
  //
  // O wrapper externo muda; o body (header + scroll content) é o mesmo.
  // [[project-caderno-realista]]
  const isModal = viewMode === "modal";

  const isSide = !isModal && !isPopoutWindow;

  const wrapperClass = isModal
    ? "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
    : isPopoutWindow
      ? "flex h-screen w-full flex-col"
      : `relative shrink-0 overflow-hidden border-l border-border bg-card/40 ${
          resizing
            ? ""
            : "transition-[width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
        }`;

  // Em side mode, width controlada por estado (drag-to-resize).
  const wrapperStyle = isSide
    ? open
      ? `width:${panelWidth}px;min-width:40px;`
      : "width:40px;min-width:40px;"
    : "";

  const innerClass = isModal
    ? "relative flex h-[92vh] w-full max-w-3xl flex-col rounded-lg border border-border bg-card shadow-2xl"
    : "relative flex h-full w-full flex-col";

  return (
    <aside class={wrapperClass} style={wrapperStyle}>
      {/* Handle de resize na borda esquerda — só em side mode quando aberto */}
      {isSide && open && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Redimensionar caderno"
          onMouseDown={startResize}
          class={`absolute left-0 top-0 z-30 h-full w-1.5 cursor-ew-resize bg-transparent transition-colors hover:bg-primary/40 ${
            resizing ? "bg-primary/60" : ""
          }`}
        />
      )}
      <div class={innerClass}>
      {/* Conteúdo recolhido (rail) — fade out quando expande */}
      <div
        class={`absolute inset-0 flex flex-col items-center gap-2 py-3 transition-opacity duration-200 ${
          open ? "pointer-events-none opacity-0" : "opacity-100 delay-150"
        }`}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          class="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Expandir caderno"
          aria-label="Expandir caderno da redação"
        >
          <PanelRightOpen className="size-4" />
        </button>
        <div class="flex flex-col items-center gap-1.5 text-muted-foreground">
          <BookOpen className="size-4" />
          <span
            class="text-[10px] font-bold uppercase tracking-wider"
            style="writing-mode:vertical-rl;transform:rotate(180deg)"
          >
            Caderno
          </span>
        </div>
      </div>

      {/* Conteúdo expandido — fade in quando abre */}
      <div
        class={`flex h-full min-w-[420px] flex-col gap-2 p-3 transition-opacity duration-200 ${
          open ? "opacity-100 delay-150" : "pointer-events-none opacity-0"
        }`}
      >
      <header class="flex items-center justify-between gap-2 border-b border-border pb-2">
        <div class="flex min-w-0 items-center gap-1.5">
          <BookOpen className="size-4 text-violet-500" />
          <span class="text-xs font-bold uppercase tracking-wider">Caderno</span>
          {banca && (
            <span class="ml-1 shrink-0 rounded bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
              {banca}
            </span>
          )}
        </div>
        <div class="flex items-center gap-0.5">
          {!isPopoutWindow && viewMode !== "modal" && (
            <button
              type="button"
              onClick={openInModal}
              class="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Abrir como modal (tela cheia nesta aba)"
              aria-label="Abrir como modal"
            >
              <Maximize2 className="size-3.5" />
            </button>
          )}
          {!isPopoutWindow && (
            <button
              type="button"
              onClick={openInPopout}
              class="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Abrir em janela à parte"
              aria-label="Abrir em janela à parte"
            >
              <PopoutIcon className="size-3.5" />
            </button>
          )}
          {viewMode === "modal" ? (
            <button
              type="button"
              onClick={closeModal}
              class="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Fechar modal e voltar à lateral"
              aria-label="Fechar modal"
            >
              <X className="size-4" />
            </button>
          ) : !isPopoutWindow ? (
            <button
              type="button"
              onClick={() => setOpen(false)}
              class="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Recolher caderno"
              aria-label="Recolher caderno"
            >
              <PanelRightClose className="size-4" />
            </button>
          ) : null}
        </div>
      </header>

      <div class="flex-1 space-y-2 overflow-y-auto pr-1">
        {/* Header do tema quando vier da biblioteca */}
        {fromLibrary && (
          <div class="rounded-md border border-border bg-background px-3 py-2 text-center">
            <p class="text-xs font-bold">{headerTitle}</p>
            {meta?.applicationDate && (
              <p class="text-[10px] text-muted-foreground">
                Aplicada em {fmtDate(meta.applicationDate)}
              </p>
            )}
            {meta?.originalSourceUrl && (
              <a
                href={meta.originalSourceUrl}
                target="_blank"
                rel="noreferrer"
                class="mt-1 inline-flex items-center gap-1 text-[10px] text-violet-600 hover:underline dark:text-violet-400"
              >
                Ver caderno oficial <ExternalLink className="size-2.5" />
              </a>
            )}
          </div>
        )}

        {/* Instruções oficiais (se tiver) */}
        {meta?.officialInstructions && (
          <Section
            id="instructions"
            title="Instruções"
            icon={BookOpen}
            sections={sections}
            setSections={setSections}
            accent="amber"
            defaultOpen={true}
          >
            <div
              class="essay-paper"
              dangerouslySetInnerHTML={{
                __html: renderEssayMarkdown(meta.officialInstructions)
              }}
            />
          </Section>
        )}

        {/* Proposta de redação */}
        {meta?.statement && (
          <Section
            id="statement"
            title="Proposta de Redação"
            icon={Sparkles}
            sections={sections}
            setSections={setSections}
            accent="violet"
            defaultOpen={true}
          >
            <div
              class="essay-paper"
              dangerouslySetInnerHTML={{
                __html: renderEssayMarkdown(meta.statement)
              }}
            />
          </Section>
        )}

        {/* Direcionadores */}
        {meta?.directives && meta.directives.length > 0 && (
          <Section
            id="directives"
            title="Direcionadores"
            icon={FileText}
            sections={sections}
            setSections={setSections}
            defaultOpen={true}
          >
            <ul class="essay-ul essay-paper">
              {meta.directives.map((d, i) => (
                <li
                  key={i}
                  dangerouslySetInnerHTML={{
                    __html: renderEssayMarkdown(d).replace(/^<p[^>]*>|<\/p>$/g, "")
                  }}
                />
              ))}
            </ul>
          </Section>
        )}

        {/* Fragmentos */}
        {fragments.length === 0 && !meta?.officialInstructions && !meta?.statement && (
          <div class="rounded-md border border-dashed border-border bg-background p-4 text-center text-xs text-muted-foreground">
            <BookOpen className="mx-auto mb-1.5 size-5 opacity-50" />
            Sem caderno anexo. Você definiu o tema livremente.
          </div>
        )}

        {fragments.length > 0 && (
          <div class="pt-1">
            <p class="mb-1.5 px-1 text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">
              Coletânea ({fragments.length})
            </p>
            <div class="space-y-2">
              {fragments.map((f) => (
                <Section
                  key={f.id}
                  id={`frag-${f.id}`}
                  title={f.label || "Fragmento"}
                  icon={fragmentIcon(f.kind)}
                  sections={sections}
                  setSections={setSections}
                  defaultOpen={true}
                >
                  <FragmentBody fragment={f} />
                  {f.source && <FonteLine source={f.source} />}
                </Section>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
      </div>
    </aside>
  );
}
